// Time helpers. Timestamps are ISO-8601 UTC with milliseconds (what
// Date#toISOString produces; the Go side formats identically). Date-only
// fields are local calendar dates and are never converted.

export const nowISO = (): string => new Date().toISOString()

export const formatTime = (d: Date): string => d.toISOString()

const pad = (n: number) => String(n).padStart(2, '0')

/** Local calendar date of d (device zone). */
export const dateOf = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

export const todayLocal = (): string => dateOf(new Date())

/** Parse YYYY-MM-DD as local midnight. */
export const parseDate = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

export const isDate = (s: unknown): boolean => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)

export const addDays = (date: string, n: number): string => {
  const d = parseDate(date)
  d.setDate(d.getDate() + n)
  return dateOf(d)
}

export const daysBetween = (a: string, b: string): number =>
  Math.round((parseDate(b).getTime() - parseDate(a).getTime()) / 86_400_000)

export const minutesBetween = (startISO: string, endISO: string): number =>
  Math.round((new Date(endISO).getTime() - new Date(startISO).getTime()) / 60_000)

export const hoursAgo = (iso: string | undefined, now = new Date()): number =>
  iso ? (now.getTime() - new Date(iso).getTime()) / 3_600_000 : Infinity

export const fmtMinutes = (m: number): string => {
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r ? `${h}h ${r}m` : `${h}h`
}

export const fmtElapsed = (startISO: string, now = Date.now()): string => {
  const s = Math.max(0, Math.floor((now - new Date(startISO).getTime()) / 1000))
  const m = Math.floor(s / 60)
  const h = Math.floor(m / 60)
  if (h > 0) return `${h}:${pad(m % 60)}:${pad(s % 60)}`
  return `${m}:${pad(s % 60)}`
}

/** How long ago, in words: "just now", "4m ago", "3h ago", "2d ago". fmtElapsed is a running clock, not this. */
export const fmtAgo = (iso: string, now = Date.now()): string => {
  const m = Math.floor(Math.max(0, now - new Date(iso).getTime()) / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

/** Friendly date words used by quick-add and defer: today, tomorrow, mon..sun, +N, YYYY-MM-DD, MM-DD, D/M. */
export const parseFriendlyDate = (raw: string, today = todayLocal()): string | null => {
  const s = raw.trim().toLowerCase()
  if (!s) return null
  if (isDate(s)) return s
  if (s === 'today' || s === 'tod') return today
  if (s === 'tomorrow' || s === 'tom' || s === 'tmr') return addDays(today, 1)
  if (s === 'yesterday') return addDays(today, -1)
  const rel = /^([+-])(\d{1,3})$/.exec(s)
  if (rel) return addDays(today, (rel[1] === '-' ? -1 : 1) * Number(rel[2]))
  const wd = WEEKDAYS.findIndex(
    (w) =>
      s === w ||
      s === w + 'day' ||
      (w === 'tue' && s === 'tuesday') ||
      (w === 'wed' && s === 'wednesday') ||
      (w === 'thu' && s === 'thursday') ||
      (w === 'sat' && s === 'saturday'),
  )
  if (wd >= 0) {
    const t = parseDate(today)
    let delta = (wd - t.getDay() + 7) % 7
    if (delta === 0) delta = 7 // "next monday" semantics: never today
    return addDays(today, delta)
  }
  const md = /^(\d{1,2})[-/](\d{1,2})$/.exec(s)
  if (md) {
    const t = parseDate(today)
    const y = t.getFullYear()
    // accept both MM-DD and D/M: slash = day/month (UK), dash = month-day (ISO-ish)
    const [a, b] = [Number(md[1]), Number(md[2])]
    const [month, day] = s.includes('/') ? [b, a] : [a, b]
    if (month < 1 || month > 12 || day < 1 || day > 31) return null
    let out = `${y}-${pad(month)}-${pad(day)}`
    if (out < today) out = `${y + 1}-${pad(month)}-${pad(day)}`
    return out
  }
  return null
}

/** A relative day inside a phrase ("due yesterday", "next tomorrow"): lower case; dates and weekdays as they are. */
export const inPhrase = (day: string): string =>
  day === 'Today' || day === 'Tomorrow' || day === 'Yesterday' ? day.toLowerCase() : day

export const fmtDateShort = (date: string, today = todayLocal()): string => {
  if (date === today) return 'Today'
  if (date === addDays(today, 1)) return 'Tomorrow'
  if (date === addDays(today, -1)) return 'Yesterday'
  const d = parseDate(date)
  const diff = daysBetween(today, date)
  if (diff > 0 && diff < 7) return d.toLocaleDateString(undefined, { weekday: 'long' })
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** fmtDateShort inside a phrase: "due yesterday", "next tomorrow" (a weekday or a date keeps its capital). */
export const fmtDateInPhrase = (date: string, today = todayLocal()): string => {
  return inPhrase(fmtDateShort(date, today))
}

/** This weekend: today when it is the weekend, else the coming Saturday. */
export const weekendOf = (today: string): string => {
  const d = parseDate(today).getDay()
  return d === 6 || d === 0 ? today : addDays(today, 6 - d)
}

/** Next week: the coming Monday. */
export const nextWeekOf = (today: string): string => {
  const d = parseDate(today).getDay()
  return addDays(today, (8 - d) % 7 || 7)
}

/**
 * The one set of day choices, wherever a day is given (quick add's "When?"
 * chips, a task's Reschedule): Today, Tomorrow, This weekend, Next week, and
 * then a picker for anything else.
 */
export const dayChoices = (today: string): [string, string][] => [
  ['Today', today],
  ['Tomorrow', addDays(today, 1)],
  ['This weekend', weekendOf(today)],
  ['Next week', nextWeekOf(today)],
]
